import React from 'react';
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from '@headlessui/react';
import { ConversationTheme, useConversationPalette } from '../conversation/ConversationTheme';

interface ModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Called when the modal should close (backdrop click, Escape key). */
  onClose: () => void;
  /** Optional title rendered at the top. */
  title?: string;
  /** Content inside the modal panel. */
  children: React.ReactNode;
  /** Max-width class for the panel. Defaults to 'max-w-md'. */
  maxWidth?: string;
  /**
   * Optional fixed-height class for the panel (e.g. `'h-[80vh]'`). When set, the
   * panel holds that height regardless of its content, so a surface whose
   * content streams in (a chat) never reflows or pops open taller as it fills.
   * Children are expected to lay themselves out within it (e.g. a flex column
   * with `h-full`). Omit for the default content-sized panel.
   */
  heightClass?: string;
  /** Use the full mobile viewport while retaining the existing desktop panel. */
  fullScreenOnMobile?: boolean;
  /**
   * Whether the modal can be dismissed by clicking the backdrop or pressing
   * Escape. Defaults to true. Set false for modals that must be resolved via
   * an explicit in-panel control (e.g. the ending countdown, where a stray
   * backdrop tap must not be read as "continue"). When false, `onClose` is
   * only ever invoked by the panel's own controls.
   */
  dismissible?: boolean;
}

/**
 * Accessible modal dialog built on Headless UI Dialog.
 * Provides focus trap, Escape-to-close, and backdrop click support.
 */
export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  children,
  maxWidth = 'max-w-md',
  heightClass,
  fullScreenOnMobile = false,
  dismissible = true,
}) => {
  const palette = useConversationPalette();
  // Headless UI fires `onClose` for both backdrop clicks and Escape. When the
  // modal is non-dismissible we hand Dialog a no-op so neither gesture closes
  // it; the in-panel button still calls the real `onClose` directly. This also
  // covers mobile: a tap outside the panel is the touch equivalent of a
  // backdrop click and is likewise ignored.
  return (
    <Dialog open={open} onClose={dismissible ? onClose : () => {}} className="relative z-[70]">
      <DialogBackdrop transition className="fixed inset-0 bg-black/60 backdrop-blur-md transition-opacity duration-200 data-closed:opacity-0 motion-reduce:transition-none" />

      <ConversationTheme accentColor={palette?.accent} className={`fixed inset-0 flex items-center justify-center p-6 ${fullScreenOnMobile ? 'max-sm:p-0' : ''}`}>
        <DialogPanel transition
          className={`transition duration-200 data-closed:opacity-0 data-closed:translate-y-2 data-closed:scale-[0.98] data-leave:duration-150 motion-reduce:transition-none bg-surface-raised border border-transparent light:border-border rounded-2xl p-8 w-full ${maxWidth}${heightClass ? ` ${heightClass}` : ''}${fullScreenOnMobile ? ' max-sm:flex max-sm:h-dvh max-sm:max-w-none max-sm:flex-col max-sm:overflow-hidden max-sm:rounded-none max-sm:border-0 max-sm:p-0' : ''}`}
        >
          {title && (
            <DialogTitle className={`text-xl font-bold text-foreground mb-4 ${fullScreenOnMobile ? 'max-sm:mb-0 max-sm:shrink-0 max-sm:px-4 max-sm:pb-3 max-sm:pt-4' : ''}`}>
              {title}
            </DialogTitle>
          )}
          {children}
        </DialogPanel>
      </ConversationTheme>
    </Dialog>
  );
};
